import { useSeoMeta } from '@unhead/react';
import { Link, useParams } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import {
  Terminal,
  Plus,
  ExternalLink,
  Copy,
  Check,
  User,
  Zap,
  UserCheck,
  UserPlus,
  Loader2,
  Pencil,
} from 'lucide-react';
import { useState } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { LoginArea } from '@/components/auth/LoginArea';
import { SkillBrowser } from '@/components/SkillBrowser';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { genUserName } from '@/lib/genUserName';

export default function Profile() {
  const { npub } = useParams<{ npub: string }>();
  const { user } = useCurrentUser();
  const [copied, setCopied] = useState(false);

  // Decode npub to hex pubkey
  let pubkey: string | null = null;
  try {
    if (npub) {
      const decoded = nip19.decode(npub);
      if (decoded.type === 'npub') {
        pubkey = decoded.data;
      } else if (decoded.type === 'nprofile') {
        pubkey = decoded.data.pubkey;
      }
    }
  } catch {
    // Invalid npub
  }

  const author = useAuthor(pubkey || '');
  const metadata = author.data?.metadata;
  const displayName = metadata?.name || (pubkey ? genUserName(pubkey) : 'Unknown');

  useSeoMeta({
    title: pubkey ? `${displayName}'s Skills - SkillForge` : 'Profile - SkillForge',
    description: pubkey ? `View skills published by ${displayName}` : 'User profile on SkillForge.',
  });

  const handleCopy = async () => {
    if (npub) {
      await navigator.clipboard.writeText(npub);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!pubkey) {
    return (
      <div className="min-h-screen bg-background">
        <ProfileHeader />
        <main className="container mx-auto px-4 py-20">
          <div className="max-w-md mx-auto text-center">
            <User className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <h1 className="text-2xl font-bold mb-2">Invalid profile</h1>
            <p className="text-muted-foreground mb-6">
              The profile link appears to be invalid.
            </p>
            <Link to="/browse">
              <Button variant="outline">Browse Skills</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const isOwnProfile = user?.pubkey === pubkey;

  return (
    <div className="min-h-screen bg-background">
      <ProfileHeader />

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Profile Header */}
          <Card className="mb-8 overflow-hidden">
            {/* Banner */}
            {metadata?.banner ? (
              <div className="h-32 sm:h-48 bg-cover bg-center" style={{ backgroundImage: `url(${metadata.banner})` }} />
            ) : (
              <div className="h-32 sm:h-48 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent" />
            )}

            <CardContent className="relative pt-0 pb-6">
              {/* Avatar */}
              <div className="absolute -top-12 left-6">
                <Avatar className="w-24 h-24 border-4 border-background">
                  <AvatarImage src={metadata?.picture} alt={displayName} />
                  <AvatarFallback className="text-2xl bg-secondary">
                    {displayName[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </div>

              <div className="pt-16 sm:pt-14 sm:pl-32 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold">{displayName}</h1>
                  {metadata?.about && (
                    <p className="text-muted-foreground mt-1 max-w-xl">
                      {metadata.about}
                    </p>
                  )}

                  {/* npub */}
                  <div className="flex items-center gap-2 mt-3">
                    <code className="text-xs text-muted-foreground font-mono bg-secondary px-2 py-1 rounded">
                      {npub?.slice(0, 12)}...{npub?.slice(-8)}
                    </code>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCopy}>
                      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    </Button>
                  </div>

                  {/* Links */}
                  <div className="flex flex-wrap gap-4 mt-4 text-sm">
                    {metadata?.website && (
                      <a
                        href={metadata.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Website
                      </a>
                    )}
                    {metadata?.lud16 && (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Zap className="w-3 h-3 text-yellow-500" />
                        {metadata.lud16}
                      </span>
                    )}
                  </div>
                </div>

                {isOwnProfile ? (
                  <div className="flex items-center gap-2">
                    <Link to="/my-skills">
                      <Button variant="outline" className="gap-2 border-white/[0.1]">
                        <Pencil className="w-4 h-4" />
                        Manage Skills
                      </Button>
                    </Link>
                    <Link to="/publish">
                      <Button className="gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 border-0">
                        <Plus className="w-4 h-4" />
                        Publish
                      </Button>
                    </Link>
                  </div>
                ) : (
                  pubkey && <FollowButton targetPubkey={pubkey} />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Skills */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4">Published Skills</h2>
          </div>

          <SkillBrowser author={pubkey} limit={50} />
        </div>
      </main>
    </div>
  );
}

function FollowButton({ targetPubkey }: { targetPubkey: string }) {
  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const { mutate: publish, isPending } = useNostrPublish();
  const queryClient = useQueryClient();

  // Fetch current user's contact list (kind 3)
  const { data: contactList } = useQuery({
    queryKey: ['contacts', user?.pubkey],
    queryFn: async (c) => {
      if (!user?.pubkey) return null;
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(3000)]);
      const events = await nostr.query(
        [{ kinds: [3], authors: [user.pubkey], limit: 1 }],
        { signal }
      );
      return events[0] ?? null;
    },
    enabled: !!user?.pubkey,
  });

  if (!user) return null;

  const followTags: string[][] = contactList?.tags ?? [];
  const isFollowing = followTags.some(([t, pk]) => t === 'p' && pk === targetPubkey);

  const handleToggle = () => {
    let newTags: string[][];

    if (isFollowing) {
      // Remove from follows
      newTags = followTags.filter(([t, pk]) => !(t === 'p' && pk === targetPubkey));
    } else {
      // Add to follows
      newTags = [...followTags, ['p', targetPubkey]];
    }

    publish(
      { kind: 3, content: contactList?.content ?? '', tags: newTags },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['contacts', user.pubkey] });
        },
      }
    );
  };

  return (
    <Button
      onClick={handleToggle}
      disabled={isPending}
      variant={isFollowing ? 'outline' : 'default'}
      className={`gap-2 min-w-[120px] ${
        isFollowing
          ? 'border-white/[0.12] bg-white/[0.04] hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-400 transition-colors group'
          : 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 border-0 shadow-lg shadow-orange-500/20'
      }`}
    >
      {isPending ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isFollowing ? (
        <>
          <UserCheck className="w-4 h-4 group-hover:hidden" />
          <User className="w-4 h-4 hidden group-hover:block" />
          <span className="group-hover:hidden">Following</span>
          <span className="hidden group-hover:block">Unfollow</span>
        </>
      ) : (
        <>
          <UserPlus className="w-4 h-4" />
          Follow
        </>
      )}
    </Button>
  );
}

function ProfileHeader() {
  const { user } = useCurrentUser();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center glow-primary-sm">
            <Terminal className="w-4 h-4 text-primary" />
          </div>
          <span className="font-semibold text-lg">SkillForge</span>
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
          <Link to="/browse" className="hover:text-foreground transition-colors">Browse</Link>
          <Link to="/publish" className="hover:text-foreground transition-colors">Publish</Link>
        </nav>

        <div className="flex items-center gap-3">
          {user && (
            <Link to="/publish">
              <Button size="sm" className="gap-2">
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Publish</span>
              </Button>
            </Link>
          )}
          <LoginArea className="max-w-60" />
        </div>
      </div>
    </header>
  );
}
